import _ from 'lodash'
import type { Node, DataNode, Observers } from './types'
import type { Ref } from '@wix/thunderbolt-types'
import type { DataNode as CatharsisDataNode, CompNode as CatharsisCompNode } from '@wix/thunderbolt-catharsis'

export const COMPS_ROOT_NAMESPACE = 'comps-root'

export function CompNodeAdapter(nodeName: string, compNode: CatharsisCompNode<any, any, any>, refs: any) {
	const newNode: Node = {
		name: nodeName,
		execute: (component, observers) => {
			const deps = compNode.getDependencies(component, refs)
			// Cannot proxy values or undefined
			if (!deps || typeof deps !== 'object') {
				return compNode.toViewItem(component, deps)
			}
			// If the node returned a single ref, not wrapped with an object, we cannot proxy it.
			if ('$type' in deps) {
				const resolvedDep = resolveRef(deps, observers)
				return compNode.toViewItem(component, resolvedDep)
			}
			const depsProxy = new Proxy(deps, {
				get(originalDeps, depKey) {
					if (originalDeps.$dirty?.has(depKey)) {
						return Reflect.get(originalDeps, depKey)
					}
					return resolveDeps(originalDeps[depKey], observers)
				},
				set(originalDeps, depKey, value) {
					originalDeps[depKey] = value
					originalDeps.$dirty ??= new Set()
					originalDeps.$dirty.add(depKey)
					return true
				},
			})
			return compNode.toViewItem(component, depsProxy)
		},
	}
	return newNode
}

export function DataNodeAdapter(dataNode: CatharsisDataNode<any, any>) {
	const newNode: DataNode<any> = {
		execute: (dataItem, resolvedRefs, observers) => {
			const deps = dataNode.getDependencies(dataItem)
			// Cannot proxy values or undefined
			if (!deps || typeof deps !== 'object') {
				return dataNode.toViewItem(dataItem, resolvedRefs, deps)
			}
			// If the node returned a single ref, not wrapped with an object, we cannot proxy it.
			if ('$type' in deps) {
				const resolvedDep = resolveRef(deps, observers)
				return dataNode.toViewItem(dataItem, resolvedRefs, resolvedDep)
			}
			const depsProxy = new Proxy(deps, {
				get(originalDeps, depKey) {
					return resolveDeps(originalDeps[depKey], observers)
				},
			})
			return dataNode.toViewItem(dataItem, resolvedRefs, depsProxy)
		},
	}
	return newNode
}

// ToDo: Use nested proxies!
function resolveDeps(deps: any, observers: Observers): any {
	if (Array.isArray(deps)) {
		return deps.map((dep) => resolveDeps(dep, observers))
	}
	if (deps && typeof deps === 'object') {
		if ('refPath' in deps) {
			return resolveRef(deps, observers)
		} else {
			return _.mapValues(deps, (dep: any) => resolveDeps(dep, observers))
		}
	}
	return deps
}

function resolveRef(refObj: Ref<any>, observers: Observers) {
	const path = refObj.refPath
	if (path[0] === 'data') {
		return observers.useData(refObj)
	}
	if (path[0] === 'svgStore' || path[0] === 'QA' || path[0] === 'selectorStore') {
		return observers.useEnv({ ...refObj, refPath: ['environment', ...path] })
	}
	if (path[0] === 'environment') {
		return observers.useEnv(refObj)
	}
	if (path[0] === COMPS_ROOT_NAMESPACE && path[1] !== 'modes') {
		const [, nodeId, compId] = path
		return observers.useNode(nodeId, compId)
	}
	if (path[1] === 'modes') {
		return observers.useEnv({ ...refObj, refPath: ['environment', ...path.slice(1)] })
	}
	throw new Error(`Unknown path ${path}`)
}
